import { CheckOutlined, DeleteOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Card, Drawer, Popconfirm, Space, Spin, Typography } from "antd";
import type { TableProps } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiFetch } from "../api/client";
import { formatDateTime } from "../api/format";
import { useServerNotifications } from "../api/notifications";
import type {
  ListResponse,
  MessageDetail,
  MessageRow,
  NotificationsResponse,
} from "../api/types";
import { DataTable } from "../components/DataTable";

/**
 * Sidebar entry "messages" (navigation.messages) → /message/index.
 *
 * The endpoints take a single id in the path, and the helper behind them binds
 * it into an `in (?)` — a comma separated list would be bound as one string and
 * silently match nothing. A selection is therefore sent as one request per row;
 * a mailbox holds tens of messages, not thousands.
 */
export function MessagesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const notify = useServerNotifications();

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [openId, setOpenId] = useState<number | null>(null);

  const messages = useQuery({
    queryKey: ["messages"],
    queryFn: () => apiFetch<ListResponse<MessageRow>>("/message/manage_message_grid"),
  });

  const detail = useQuery({
    queryKey: ["messages", "detail", openId],
    queryFn: () => apiFetch<MessageDetail>(`/message/message_detail/${openId}`),
    enabled: openId !== null,
  });

  // The unread count in the sidebar is derived server-side, so it has to be
  // refetched together with the list.
  const refresh = async (results: NotificationsResponse[]) => {
    results.forEach((result) => notify(result.notifications));
    setSelectedIds([]);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["messages"] }),
      queryClient.invalidateQueries({ queryKey: ["sidebar"] }),
    ]);
  };

  const markAsRead = useMutation({
    mutationFn: (ids: number[]) =>
      Promise.all(
        ids.map((id) =>
          apiFetch<NotificationsResponse>(`/message/message_mark_as_read/${id}`),
        ),
      ),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: (ids: number[]) =>
      Promise.all(
        ids.map((id) =>
          apiFetch<NotificationsResponse>(`/message/${id}`, { method: "DELETE" }),
        ),
      ),
    onSuccess: async (results, ids) => {
      if (openId !== null && ids.includes(openId)) setOpenId(null);
      await refresh(results);
    },
  });

  const columns: TableProps<MessageRow>["columns"] = useMemo(
    () => [
      {
        title: "",
        dataIndex: "viewed_at",
        width: 40,
        align: "center",
        render: (viewedAt: string | null) => (viewedAt ? null : <Badge status="processing" />),
      },
      {
        title: t("activerecord.attributes.notification.created_at"),
        dataIndex: "created_at",
        width: 170,
        defaultSortOrder: "descend",
        sorter: (a, b) => (a.created_at ?? "").localeCompare(b.created_at ?? ""),
        render: (createdAt: string | null, row) => (
          <Typography.Text strong={!row.viewed_at}>{formatDateTime(createdAt)}</Typography.Text>
        ),
      },
      {
        title: t("activerecord.attributes.notification.subject"),
        dataIndex: "subject",
        ellipsis: true,
        render: (subject: string | null, row) => (
          <Typography.Text strong={!row.viewed_at}>{subject}</Typography.Text>
        ),
      },
    ],
    [t],
  );

  const busy = markAsRead.isPending || remove.isPending;
  const openMessage = messages.data?.data.find((row) => row.id === openId) ?? null;

  return (
    <Card title={t("navigation.messages")}>
      <Space style={{ marginBottom: 16 }}>
        <Button
          icon={<CheckOutlined />}
          disabled={selectedIds.length === 0}
          loading={markAsRead.isPending}
          onClick={() => markAsRead.mutate(selectedIds)}
        >
          {t("notification.mark_as_read")}
        </Button>

        <Popconfirm
          title={t("notification.delete_notification")}
          // Both button texts spelled out, like every modal.confirm in the app:
          // they are otherwise antd's own, and this is the only place in the
          // application where that shows.
          okText={t("dialog.answer_yes")}
          cancelText={t("dialog.answer_no")}
          disabled={selectedIds.length === 0}
          onConfirm={() => remove.mutate(selectedIds)}
        >
          <Button
            danger
            icon={<DeleteOutlined />}
            disabled={selectedIds.length === 0}
            loading={remove.isPending}
          >
            {t("notification.delete_notification")}
          </Button>
        </Popconfirm>
      </Space>

      <DataTable
        columns={columns}
        rows={messages.data?.data}
        loading={messages.isPending || busy}
        error={messages.error ?? markAsRead.error ?? remove.error}
        onRowClick={(row) => setOpenId(row.id)}
        rowSelection={{
          selectedRowKeys: selectedIds,
          onChange: (keys) => setSelectedIds(keys.map(Number)),
        }}
        exportAs={t("navigation.messages")}
      />

      <Drawer
        open={openId !== null}
        onClose={() => setOpenId(null)}
        size={860}
        destroyOnHidden
        title={openMessage?.subject}
        extra={
          openMessage &&
          !openMessage.viewed_at && (
            <Button
              icon={<CheckOutlined />}
              loading={markAsRead.isPending}
              onClick={() => markAsRead.mutate([openMessage.id])}
            >
              {t("notification.mark_as_read")}
            </Button>
          )
        }
      >
        {detail.isPending ? (
          <Spin />
        ) : (
          // The body is the stored mail: markup that was assembled from user
          // supplied names, congregations and free text. It is rendered in a
          // fully sandboxed iframe rather than injected into this document, so
          // nothing in it can run script or reach the JWT — that is also why no
          // HTML sanitizer is needed here.
          <iframe
            title={openMessage?.subject ?? ""}
            sandbox=""
            srcDoc={detail.data?.data ?? ""}
            style={{
              width: "100%",
              height: "70vh",
              border: "1px solid #f0f0f0",
              background: "#fff",
            }}
          />
        )}
      </Drawer>
    </Card>
  );
}
